import {
  CAT_RUNNING_CHURU_BUNDLE_GAP_X,
  CAT_RUNNING_CHURU_BUNDLE_OFFSET_MAX_Y,
  CAT_RUNNING_CHURU_BUNDLE_OFFSET_MIN_Y,
  CAT_RUNNING_CHURU_HEIGHT,
  CAT_RUNNING_CHURU_LEVEL_Y,
  CAT_RUNNING_CHURU_MAX_SPAWN_MS,
  CAT_RUNNING_CHURU_MIN_SPAWN_MS,
  CAT_RUNNING_CHURU_SPAWN_X,
  CAT_RUNNING_CHURU_WIDTH,
} from '../../constants'
import { getChuruHitbox, getObstacleHitbox, isRectOverlap } from '../collision'

const getRandomBetween = (min, max, randomFn = Math.random) => min + randomFn() * (max - min)
const MAX_CHURU_SPAWN_ATTEMPTS = 6

export const getRandomChuruSpawnMs = () =>
  getRandomBetween(CAT_RUNNING_CHURU_MIN_SPAWN_MS, CAT_RUNNING_CHURU_MAX_SPAWN_MS)

export const getRandomChuruSpawnMsWith = (randomFn) =>
  getRandomBetween(CAT_RUNNING_CHURU_MIN_SPAWN_MS, CAT_RUNNING_CHURU_MAX_SPAWN_MS, randomFn)

export const getRandomChuruBundleSize = (randomFn = Math.random) => {
  const roll = randomFn()

  if (roll < 0.5) return 1
  if (roll < 0.85) return 2
  return 3
}

const getRandomBaseY = (direction, randomFn = Math.random) => {
  if (direction < 0) {
    return CAT_RUNNING_CHURU_LEVEL_Y[CAT_RUNNING_CHURU_LEVEL_Y.length - 1]
  }

  return CAT_RUNNING_CHURU_LEVEL_Y[Math.floor(randomFn() * CAT_RUNNING_CHURU_LEVEL_Y.length)]
}

const getRandomBundleDirection = (randomFn = Math.random) => {
  const roll = randomFn()

  if (roll < 1 / 3) return -1
  if (roll < 2 / 3) return 0
  return 1
}

const buildChuruBundle = (world, bundleSize = getRandomChuruBundleSize(world.randomFn)) => {
  const randomFn = world.randomFn ?? Math.random
  const direction = bundleSize === 1 ? 0 : getRandomBundleDirection(randomFn)
  const baseY = getRandomBaseY(direction, randomFn)
  const stepY = getRandomBetween(
    CAT_RUNNING_CHURU_BUNDLE_OFFSET_MIN_Y,
    CAT_RUNNING_CHURU_BUNDLE_OFFSET_MAX_Y,
    randomFn,
  )
  const bundle = []

  for (let index = 0; index < bundleSize; index += 1) {
    const offsetY = direction === 0 ? 0 : stepY * index * direction
    const floatTime = randomFn() * Math.PI * 2

    bundle.push({
      id: `${world.elapsedMs}-churu-${index}-${randomFn().toString(36).slice(2, 8)}`,
      x: CAT_RUNNING_CHURU_SPAWN_X + index * (CAT_RUNNING_CHURU_WIDTH + CAT_RUNNING_CHURU_BUNDLE_GAP_X),
      previousX: CAT_RUNNING_CHURU_SPAWN_X + index * (CAT_RUNNING_CHURU_WIDTH + CAT_RUNNING_CHURU_BUNDLE_GAP_X),
      y: baseY - offsetY,
      previousY: baseY - offsetY,
      width: CAT_RUNNING_CHURU_WIDTH,
      height: CAT_RUNNING_CHURU_HEIGHT,
      floatTime,
      previousFloatTime: floatTime,
    })
  }

  return bundle
}

const isChuruBundlePlacementBlocked = (world, bundle) => {
  const obstacleHitboxes = world.obstacles.map((obstacle) => getObstacleHitbox(obstacle))
  const activeChuruHitboxes = world.churus.map((churu) => getChuruHitbox(churu))

  return bundle.some((churu) => {
    const candidateHitbox = getChuruHitbox(churu)

    return (
      obstacleHitboxes.some((obstacleHitbox) => isRectOverlap(candidateHitbox, obstacleHitbox)) ||
      activeChuruHitboxes.some((churuHitbox) => isRectOverlap(candidateHitbox, churuHitbox))
    )
  })
}

export const spawnChuruBundle = (world) => {
  for (let attempt = 0; attempt < MAX_CHURU_SPAWN_ATTEMPTS; attempt += 1) {
    const bundle = buildChuruBundle(world)

    if (isChuruBundlePlacementBlocked(world, bundle)) {
      continue
    }

    world.churus.push(...bundle)
    return true
  }

  return false
}

const spawnScriptedChuruBundle = (world, count) => {
  const bundle = buildChuruBundle(world, Math.max(1, Number(count) || 1))
  if (isChuruBundlePlacementBlocked(world, bundle)) {
    return false
  }

  world.churus.push(...bundle)
  return true
}

export const updateChuruSpawner = (world, deltaSec) => {
  const scriptedSpawns = world.spawnManifest?.churuSpawns

  if (Array.isArray(scriptedSpawns) && scriptedSpawns.length > 0) {
    while (world.nextScriptedChuruIndex < scriptedSpawns.length) {
      const nextSpawn = scriptedSpawns[world.nextScriptedChuruIndex]
      if (!Number.isFinite(nextSpawn?.tSec) || world.elapsedMs / 1000 < nextSpawn.tSec) {
        break
      }

      spawnScriptedChuruBundle(world, nextSpawn.count)
      world.nextScriptedChuruIndex += 1
    }
    return
  }

  world.spawnTimers.churuMs -= deltaSec * 1000

  while (world.spawnTimers.churuMs <= 0) {
    spawnChuruBundle(world)
    world.spawnTimers.churuMs += getRandomChuruSpawnMsWith(world.randomFn)
  }
}
